/**
 * Contact Page - WoodAlps Customer Communication Hub
 * 
 * This page provides comprehensive contact information and communication options:
 * - Advanced contact form with spam protection
 * - Workshop location and visit information
 * - Social media integration (Instagram, Facebook)
 * - Contact details and business hours
 * - Form submission success state management
 * - Responsive design for all screen sizes
 * 
 * Contact Methods:
 * - Contact form with validation and security
 * - Physical workshop address for visits
 * - Social media channels for engagement
 * - Professional contact information display
 * 
 * Features:
 * - Form success state with confirmation message
 * - Security features including Turnstile protection
 * - Mobile-responsive design and layout
 * - SEO optimization for local search
 * - Accessible form design and navigation
 * 
 * @description Customer contact page with form, location, and social media integration
 */

import Head from 'next/head';
import Layout from '../components/layout/Layout';
import { MapPin, InstagramIcon, FacebookIcon } from 'lucide-react';
import ContactForm from '../components/forms/ContactForm';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from '../src/hooks/useTranslation';

// Dynamically import the Leaflet map component to avoid SSR issues
const LeafletMap = dynamic(() => import('../components/ui/LeafletMap'), {
  ssr: false,
  loading: () => <div className="w-full max-w-[600px] h-[300px] md:h-[600px] mx-auto rounded-xl bg-gray-200 flex items-center justify-center">Loading map...</div>
});

export default function Contact() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { t } = useTranslation();
  const handleFormSuccess = () => {
    setFormSubmitted(true);
  };

  return (
    <>
      <Head>
        <title>{t('contact.tab_title')} - WOODALPS</title>
        <meta
          name="description"
          content={t('contact.meta_description')}
        />
        <link 
          rel="stylesheet" 
          href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
          integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
          crossOrigin=""
        />
      </Head>

      <div className="bg-white pt-0 ">
        {/* Page Header */}
        <div className="bg-wood-brown/20 text-black pt-[10vh] pb-6 md:pb-10">
          <div className="container-custom text-center">
            <h1 className="text-2xl md:text-3xl lg:text-3xl font-bold mb-10 font-neuropol">{t('contact.title')}</h1>
          </div>
          <div className="w-1/6 h-1 bg-accent-color mt-4 mx-auto"></div>
        </div>

        <div className="container-custom py-12 md:py-16">
          {/* Contact Form Section - Mobile responsive */}
          <div className="max-w-4xl mx-auto mb-12 px-4 sm:px-0">
            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl">
              {!formSubmitted && (
                <h2 className="text-xl sm:text-2xl font-bold mb-6 font-neuropol text-black">{t('contact.form_title')}</h2>
              )}
              <ContactForm onFormSuccess={handleFormSuccess} />
            </div>
          </div>

          {/* Information Cards - Mobile responsive */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto mb-12 px-4 sm:px-0">
            {/* Contact Information */}
            <div>
              <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl h-full">
                <h2 className="text-xl sm:text-2xl font-bold mb-6 font-neuropol text-black">{t('contact.visit_title')}</h2>
                <div className="mb-4">
                  <p className="font-medium text-deep-black font-montserrat">{<span className="font-neuropol">WOODALPS</span>}{t('contact.visit_address')}</p>
                  <p className="text-gray-600 font-montserrat">16 Route du Champ Poirier</p>
                  <p className="text-gray-600 font-montserrat">Lugrin, 74500</p>
                  <p className="text-gray-600 font-montserrat">France</p>
                </div>
                <div className="mb-4">
                  <p className="font-medium text-deep-black font-montserrat">{t('contact.email_title')}</p>
                  <a href="mailto:contact@woodalps.com" className="text-accent-color hover:underline font-montserrat">
                    contact@woodalps.com
                  </a>
                </div>
                <div className="mb-4">
                  <p className="font-medium text-deep-black font-montserrat">{t('contact.phone_label')}</p>
                  <p className="text-gray-600 font-montserrat">+33(0)6 03 97 31 01</p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl h-full">
                <h2 className="text-xl sm:text-2xl font-bold mb-6 font-neuropol text-black">{t('contact.follow_title')}</h2>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/woodalps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 shadow-sm"
                    aria-label="Facebook"
                  >
                    <FacebookIcon className="w-6 h-6" />
                  </a>
                  <a
                    href="https://www.instagram.com/woodalps_wooden_bikes/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-pink-600 text-white hover:from-amber-600 hover:to-pink-700 transition-all duration-300 shadow-sm"
                    aria-label="Instagram"
                  >
                    <InstagramIcon className="w-6 h-6" />
                  </a>
                  
                </div>
              </div>
            </div>
          </div>
          
          {/* Map Section - Mobile responsive */}
          <div className="max-w-5xl mx-auto flex flex-col items-center px-4 sm:px-0">
            <h2 className="text-xl sm:text-2xl font-bold mb-6 font-neuropol text-black text-center">{t('contact.find_us_title')}</h2>
            <p className="mb-6 max-w-2xl text-center text-sm sm:text-base"><span className="font-neuropol">WOODALPS</span>{t('contact.find_us_text')}</p>
            <div className="w-full">
              <LeafletMap />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Define a custom getLayout function to apply the layout at the page level
Contact.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
}; 